import axios from "axios";
import { clearResourceToken } from "./resourceToken";
import { getCSRFToken } from "./_api";
import { queryClient } from "./query-client";
import {
  getImpersonation,
  setImpersonation,
  type StaffImpersonation,
} from "./impersonation-state";

export { getImpersonation };
export type { StaffImpersonation };
let initialized = false;
let leaving = false;
const changeKey = "sked-support-session-change";

export function reloadIdentity(destination = "/dashboard", notify = true) {
  if (leaving) return;
  leaving = true;
  clearResourceToken();
  queryClient.clear();
  // These stores contain account selections, drafts and legacy credentials.
  // A full navigation also discards Redux, Apollo, websockets and in-flight work.
  localStorage.clear();
  sessionStorage.clear();
  if (notify) localStorage.setItem(changeKey, String(Date.now()));
  window.location.replace(destination);
}

// False means navigation to sign-out has started; callers must stop bootstrap.
export async function initializeImpersonation(): Promise<boolean> {
  if (leaving) return false;
  if (initialized) return true;
  try {
    const { data } = await axios.get<{
      impersonation: StaffImpersonation | null;
    }>("/auth/impersonation/status");
    setImpersonation(data.impersonation);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // This runs before initAPI installs the normal 401 handler. A legacy
      // session retained after native migration must be cleared server-side too.
      reloadIdentity("/logout");
      return false;
    }
    throw error;
  }
  initialized = true;
  axios.interceptors.request.use((request) => {
    const url = new URL(
      request.url ?? "",
      request.baseURL ?? window.location.origin
    );
    if (url.origin === window.location.origin)
      request.headers.set(
        "X-Sked-Impersonation",
        getImpersonation()?.id ?? "none"
      );
    return request;
  });
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error.response?.status === 409 &&
        error.response.data?.error === "impersonation_changed"
      )
        reloadIdentity();
      return Promise.reject(error);
    }
  );
  window.addEventListener("storage", (event) => {
    if (event.key === changeKey && event.newValue)
      reloadIdentity("/dashboard", false);
  });
  const refresh = async () => {
    try {
      const result = await axios.get<{
        impersonation: StaffImpersonation | null;
      }>("/auth/impersonation/status");
      if (result.data.impersonation?.id !== getImpersonation()?.id)
        reloadIdentity();
    } catch {
      // Keep the current identity; resource authentication fails closed offline.
    }
  };
  window.addEventListener("focus", () => void refresh());
  const current = getImpersonation();
  if (current) {
    window.setInterval(() => void refresh(), 15_000);
    window.setTimeout(
      () => reloadIdentity(),
      Math.max(0, current.expiresAt - Date.now())
    );
  }
  return true;
}

export async function startImpersonation(userId: string, reason?: string) {
  const csrf = await getCSRFToken();
  try {
    await axios.post(
      "/auth/impersonation/start",
      { userId, ...(reason?.trim() ? { reason: reason.trim() } : {}) },
      { headers: { "X-XSRF-TOKEN": csrf } }
    );
  } catch (error) {
    const message = axios.isAxiosError<{ error?: unknown }>(error)
      ? error.response?.data?.error
      : undefined;
    if (typeof message === "string" && message)
      throw new Error(message, { cause: error });
    throw error;
  }
  reloadIdentity();
}

export async function stopImpersonation() {
  const csrf = await getCSRFToken();
  await axios.post(
    "/auth/impersonation/stop",
    {},
    { headers: { "X-XSRF-TOKEN": csrf } }
  );
  reloadIdentity("/admin");
}
